import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import type { CustomerType } from '@customTypes/customer';
import { getPortfolioIdFromUrl } from '@utils/getUrlParams';
import { datadogRum } from '@datadog/browser-rum';
import api from '@services/api';
import useGetPortfolioList from '@hooks/useGetPortfolioList';
import useGetCustomerTags from '@hooks/useGetCustomerTags';
import useGetPortfolio from '@hooks/useGetPortfolio';
import usePaginationParams from '@hooks/usePaginationParams';

import type {
  CustomersProviderType, CustomersProviderProps,
} from './types';

const Context = createContext({} as CustomersProviderType);
const useCustomersContext = (): CustomersProviderType => useContext(Context);

const CustomersProvider = ({ children }: CustomersProviderProps) => {
  const initialized = useRef(false);

  const [customers, setCustomers] = useState<CustomerType[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  const {
    getPortfolios,
    portfolios,
  } = useGetPortfolioList();

  const {
    getTags,
    customerTags,
  } = useGetCustomerTags();

  const { portfolio: portfolioDetails } = useGetPortfolio({
    portfolioId: getPortfolioIdFromUrl(),
  });

  const getCustomers = useCallback(async ({ ...filters }) => {
    setLoading(true);
    try {
      const response = await api.get('/customer', {
        params: {
          pageIndex: filters.page || 1,
          pageLimit: filters.limit || 10,
          portfolioId: getPortfolioIdFromUrl(),
          ...filters,
        },
      });

      setCustomers(response.data.data);
      setTotalPages(response.data.total);
    } catch (error) {
      toast.error('Erro ao carregar os clientes. Tente novamente mais tarde.');
      datadogRum.addError(new Error('Fail to load customers'), {
        error,
        portfolioId: getPortfolioIdFromUrl(),
        filters,
      });
    } finally {
      setLoading(false);
    }
  }, []);

  usePaginationParams({ onParamsChange: getCustomers });
  
  useEffect(() => {
    if (!initialized.current) {
      initialized.current = true;
      getPortfolios({});
      getTags();
    }
  }, [getPortfolios, getTags]);

  const value = useMemo(() => ({
    getCustomers,
    customers,
    totalPages,
    loading,
    showFilters,
    setShowFilters,
    customerTags,
    portfolios,
    getPortfolios,
    portfolioDetails,
  }), [
    getCustomers, customers, totalPages, loading,
    showFilters, customerTags, portfolios, getPortfolios,
    portfolioDetails,
  ]);

  return (
    <Context.Provider value={value}>
      {children}
    </Context.Provider>
  );
};

export { CustomersProvider, useCustomersContext };
